/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/app/modules/Auth/pages/AuthPage`, `src/app/BasePage`).
 */

import { FC } from "react";
import { Routes, Route, BrowserRouter } from "react-router-dom";
import { shallowEqual, useSelector } from "react-redux";
import { PrivateRoutes } from "./PrivateRoutes";
import { Logout, AuthPage, IAuthState } from "../modules/auth";
import { RootState } from "../../setup";
import { App } from "../App";
import CPL2UserLogin from "app/modules/auth/components/CPL2UserLogin";
import { selectDownForMaintenance } from "app/system/maintenance/MaintenanceSlice";
import MaintenancePage from "app/system/maintenance/MaintenancePage";
import PasswordReset from "app/modules/auth/PasswordReset";
import EmailVerification from "app/modules/auth/EmailVerification";
import LoginLinkHandler from "app/modules/auth/components/LoginLinkHandler";
import ExternalAccessLinkHandler from "app/modules/auth/components/ExternalAccessLinkHandler";
import ExternalAccessRoutes from "./ExternalAccessRoutes";
import { CPLAppRoute } from "cpl-react-common/components/Navigation/CPLAppRoute";
import { ErrorsPage } from "app/pages/errors/ErrorsPage";

/**
 * Base URL of the website.
 *
 * @see https://facebook.github.io/create-react-app/docs/using-the-public-folder
 */
const { PUBLIC_URL } = process.env;

// To remove the v7 warning in the console
const routerFuture = { v7_startTransition: false, v7_relativeSplatPath: false };

const AppRoutes: FC = () => {
  const authState = useSelector<RootState, IAuthState>(({ auth }) => auth, shallowEqual);
  const downForMaintenance = useSelector(selectDownForMaintenance);

  const getAppRoutes: () => JSX.Element = () => {
    if (downForMaintenance) {
      return CPLAppRoute({ inputPath: "maintenance", element: <MaintenancePage /> });
    }

    if (authState.externalAccess)
      return CPLAppRoute({
        inputPath: "",
        element: <ExternalAccessRoutes allowedRoute={authState.externalAccess.accessPath} />,
      });

    if (!authState.user) {
      return CPLAppRoute({ inputPath: "auth", element: <AuthPage /> });
    }

    if (authState.user.passwordChangeRequired)
      return CPLAppRoute({ inputPath: "passwordreset", element: <PasswordReset userID={authState.user.userID!} /> });

    if (!authState.user.isVerified)
      return CPLAppRoute({ inputPath: "verifyemail", element: <EmailVerification userID={authState.user.userID!} /> });

    if (!authState.user.rpmUserID) {
      return CPLAppRoute({ inputPath: "AccountLink", element: <CPL2UserLogin /> });
    }

    return CPLAppRoute({ inputPath: "", outputPath: "dashboard", isIndex: true, element: <PrivateRoutes /> });
  };

  return (
    <BrowserRouter basename={PUBLIC_URL} future={routerFuture}>
      <Routes>
        <Route element={<App />}>
          <Route path="error/*" element={<ErrorsPage />} />
          <Route path="logout" element={<Logout />} />
          <Route path="loginLink/:token" element={<LoginLinkHandler />} />
          <Route path="externalAccess/:token" element={<ExternalAccessLinkHandler />} />
          {getAppRoutes()}
        </Route>
      </Routes>
    </BrowserRouter>
  );
};

export { AppRoutes };
